import {AfterViewInit, Component, ElementRef, Inject, OnDestroy, PLATFORM_ID, ViewChild, inject, signal} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {firstValueFrom} from 'rxjs';
import {ApiContactRequest, ContactService} from '../../../apiclients/playble';
import {environment} from '../../environments/environment';
import {HttpErrorResponse} from '@angular/common/http';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

@Component({
  selector: 'app-contact-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatProgressSpinner,
  ],
  template: `
    <h2 mat-dialog-title>Contact Lewis</h2>
    <mat-dialog-content class="space-y-2 min-w-[320px] sm:min-w-[500px]">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        Send a message and I will get back to you.
      </p>

      <form [formGroup]="form" class="space-y-3">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" maxlength="120" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Email</mat-label>
          <input matInput formControlName="email" type="email" maxlength="320" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Message</mat-label>
          <textarea matInput formControlName="message" rows="5" maxlength="4000"></textarea>
        </mat-form-field>

        <div class="hidden">
          <input formControlName="website" tabindex="-1" autocomplete="off" />
        </div>

        @if (showTurnstile() && isBrowser) {
          <div #turnstileContainer></div>
        }

        @if (errorMessage()) {
          <p class="text-sm text-red-600">{{ errorMessage() }}</p>
        }

        @if (successMessage()) {
          <p class="text-sm text-emerald-600 dark:text-emerald-400">{{ successMessage() }}</p>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="gap-2">
      <button mat-button mat-dialog-close [disabled]="isLoading()">Cancel</button>
      <button mat-flat-button color="primary" (click)="submit()" [disabled]="isLoading() || form.invalid">
        @if (isLoading()) {
          <mat-spinner diameter="18" class="inline-block align-middle mr-2"></mat-spinner>
        }
        Send
      </button>
    </mat-dialog-actions>
  `
})
export class ContactDialog implements AfterViewInit, OnDestroy {
  @ViewChild('turnstileContainer') turnstileContainer?: ElementRef<HTMLElement>;

  private readonly contactService = inject(ContactService);
  private readonly dialogRef = inject(MatDialogRef<ContactDialog>);
  private readonly platformId = inject(PLATFORM_ID);

  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly isLoading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);
  readonly showTurnstile = signal(!!environment.turnstileSiteKey);

  private turnstileWidgetId: string | null = null;
  private turnstileToken: string | null = null;

  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(2), Validators.maxLength(120)] }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email, Validators.maxLength(320)] }),
    message: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(10), Validators.maxLength(4000)] }),
    website: new FormControl('', { nonNullable: true }),
  });

  async ngAfterViewInit(): Promise<void> {
    if (!this.isBrowser || !this.showTurnstile()) {
      return;
    }

    try {
      await this.ensureTurnstileLoaded();
      if (window.turnstile && this.turnstileContainer?.nativeElement) {
        this.turnstileWidgetId = window.turnstile.render(this.turnstileContainer.nativeElement, {
          sitekey: environment.turnstileSiteKey,
          callback: (token: string) => {
            this.turnstileToken = token;
            this.errorMessage.set(null);
          },
          'expired-callback': () => {
            this.turnstileToken = null;
          },
          'error-callback': () => {
            this.turnstileToken = null;
            this.errorMessage.set('Captcha failed to load. Please refresh and try again.');
          },
          theme: 'auto'
        });
      }
    } catch {
      this.errorMessage.set('Captcha failed to load. Please refresh and try again.');
    }
  }

  ngOnDestroy(): void {
    if (this.turnstileWidgetId && window.turnstile) {
      window.turnstile.remove(this.turnstileWidgetId);
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.showTurnstile() && !this.turnstileToken) {
      this.errorMessage.set('Please complete the captcha challenge.');
      return;
    }

    this.errorMessage.set(null);
    this.successMessage.set(null);
    this.isLoading.set(true);

    const payload: ApiContactRequest = {
      name: this.form.controls.name.value.trim(),
      email: this.form.controls.email.value.trim(),
      message: this.form.controls.message.value.trim(),
      turnstileToken: this.turnstileToken ?? undefined,
      website: this.form.controls.website.value.trim() || undefined,
    };

    try {
      const response = await firstValueFrom(this.contactService.apiContactSendPost(payload));
      if (response.success) {
        this.successMessage.set('Message sent.');
        setTimeout(() => this.dialogRef.close(true), 500);
        return;
      }

      this.errorMessage.set(response.error ?? 'Unable to send your message right now.');
      this.resetCaptcha();
    } catch (error) {
      const responseError = error instanceof HttpErrorResponse
        ? (error.error?.error ?? error.error?.title ?? null)
        : null;
      this.errorMessage.set(responseError || 'Unable to send your message right now.');
      this.resetCaptcha();
    } finally {
      this.isLoading.set(false);
    }
  }

  private resetCaptcha(): void {
    if (this.turnstileWidgetId && window.turnstile) {
      window.turnstile.reset(this.turnstileWidgetId);
    }
    this.turnstileToken = null;
  }

  private async ensureTurnstileLoaded(): Promise<void> {
    if (!this.isBrowser || window.turnstile) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load turnstile')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset['turnstileScript'] = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load turnstile'));
      document.head.appendChild(script);
    });
  }
}
